import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const AdminPage = () => {
  const { user } = useAuth();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [filteredQuestionnaires, setFilteredQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // New state for questionnaire types management
  const [questionnaireTypes, setQuestionnaireTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState('');

  // Load questionnaire types
  const fetchQuestionnaireTypes = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:7071/api/getQuestionnaireTypes'
        : '/api/getQuestionnaireTypes';
      
      const config = process.env.NODE_ENV === 'development' 
        ? {} 
        : { withCredentials: true };
        
      const response = await axios.get(apiUrl, config);
      
      if (response.data.success) {
        setQuestionnaireTypes(Object.values(response.data.questionnaireTypes));
      } else {
        throw new Error('Failed to load questionnaire types');
      }
    } catch (err) {
      console.error('Error loading questionnaire types:', err);
      setTypesError('Failed to fetch questionnaire types.');
    } finally {
      setTypesLoading(false);
    }
  };

  // Toggle questionnaire type visibility
  const toggleQuestionnaireVisibility = async (typeId, currentStatus) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:7071/api/updateQuestionnaireType'
        : '/api/updateQuestionnaireType';
      
      const config = process.env.NODE_ENV === 'development' 
        ? {} 
        : { withCredentials: true };
        
      await axios.post(apiUrl, {
        typeId,
        isActive: !currentStatus
      }, config);
      
      // Refresh the types
      await fetchQuestionnaireTypes();
    } catch (err) {
      console.error('Error updating questionnaire type:', err);
      alert('Erreur lors de la mise à jour du questionnaire');
    }
  };

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:7071/api/getAllQuestionnaires'
        : '/api/getAllQuestionnaires';
        const response = await axios.get(apiUrl);
        const sortedQuestionnaires = response.data.questionnaires.sort((a, b) => 
          new Date(b.lastUpdated) - new Date(a.lastUpdated)
        );
        setQuestionnaires(sortedQuestionnaires);
        setFilteredQuestionnaires(sortedQuestionnaires);
      } catch (err) {
        setError('Failed to fetch questionnaires.');
      }
      setLoading(false);
    };

    // Fetch questionnaires in development mode or for admin user
    const isDevMode = process.env.NODE_ENV === 'development';
    const isAdminUser = user && user.email === 'jean.philippe.robichaud@gmail.com';
    
    if (isDevMode || isAdminUser) {
      fetchQuestionnaires();
      fetchQuestionnaireTypes();
    }
  }, [user]);

  // Filter questionnaires based on status, search term, and questionnaire type
  useEffect(() => {
    let filtered = questionnaires;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }

    // Filter by questionnaire type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(q => {
        // Match by questionnaire type or questionnaireId
        return q.questionnaireType === typeFilter || q.questionnaireId === typeFilter;
      });
    }

    // Filter by search term (name, email, or questionnaire ID)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        (q.userName || '').toLowerCase().includes(term) ||
        (q.userId || '').toLowerCase().includes(term) ||
        (q.questionnaireId || '').toLowerCase().includes(term)
      );
    }

    setFilteredQuestionnaires(filtered);
  }, [questionnaires, statusFilter, searchTerm, typeFilter]);

  const downloadJSON = (questionnaire) => {
    const dataStr = JSON.stringify(questionnaire, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `questionnaire-${questionnaire.userId}-${questionnaire.questionnaireId}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Allow admin access in development mode or for specific admin email
  const isDevMode = process.env.NODE_ENV === 'development';
  const isAdminUser = user && user.email === 'jean.philippe.robichaud@gmail.com';
  
  if (!isDevMode && !isAdminUser) {
    return <div>Access Denied</div>;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#27ae60';
      case 'in_progress': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const getProgressPercentage = (q) => {
    return q.totalQuestions ? Math.round((q.answeredQuestions / q.totalQuestions) * 100) : 0;
  };

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      <h1 style={{ color: '#2c3e50', marginBottom: '2rem' }}>
        📊 Tableau de Bord Administrateur
        {process.env.NODE_ENV === 'development' && (
          <span style={{ 
            marginLeft: '1rem', 
            fontSize: '0.6em', 
            backgroundColor: '#f39c12', 
            color: 'white', 
            padding: '0.25rem 0.5rem', 
            borderRadius: '4px',
            fontWeight: 'normal'
          }}>
            MODE DÉVELOPPEMENT
          </span>
        )}
      </h1>

      {/* Questionnaire Types Management */}
      <div style={{ marginBottom: '3rem' }}>
        <h2 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.5rem' }}>
          🎛️ Gestion des Types de Questionnaires
        </h2>
        
        {typesLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
            Chargement des types de questionnaires...
          </div>
        ) : typesError ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}>
            {typesError}
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {questionnaireTypes.map((type) => (
              <div key={type.id} style={{
                border: `2px solid ${type.color}`,
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: type.isActive ? '#ffffff' : '#f8f9fa',
                opacity: type.isActive ? 1 : 0.7
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>{type.icon}</span>
                    <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1rem' }}>{type.name}</h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      backgroundColor: type.isActive ? '#27ae60' : '#e74c3c',
                      color: 'white'
                    }}>
                      {type.isActive ? 'ACTIF' : 'INACTIF'}
                    </span>
                  </div>
                </div>
                
                <p style={{ 
                  margin: '0.5rem 0', 
                  fontSize: '0.9rem', 
                  color: '#7f8c8d',
                  lineHeight: '1.4'
                }}>
                  {type.description}
                </p>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '0.8rem',
                  color: '#95a5a6',
                  marginBottom: '1rem'
                }}>
                  <span>{type.totalQuestions} questions • {type.duration}</span>
                  <span>{type.resultDisplayType === 'radar' ? '📊 Radar' : '📈 Scatter'}</span>
                </div>
                
                <button
                  onClick={() => toggleQuestionnaireVisibility(type.id, type.isActive)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '5px',
                    backgroundColor: type.isActive ? '#e74c3c' : '#27ae60',
                    color: 'white',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = type.isActive ? '#c0392b' : '#219a52';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = type.isActive ? '#e74c3c' : '#27ae60';
                  }}
                >
                  {type.isActive ? '🚫 Désactiver' : '✅ Activer'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Questionnaire Responses Section */}
      <h2 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.5rem' }}>
        📋 Réponses aux Questionnaires
      </h2>
      
      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            🔍 Rechercher :
          </label>
          <input
            type="text"
            placeholder="Nom, email ou questionnaire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            📈 Statut :
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <option value="all">Tous</option>
            <option value="completed">Terminés</option>
            <option value="in_progress">En cours</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            📝 Type de questionnaire :
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <option value="all">Tous les types</option>
            {questionnaireTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.icon} {type.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Statistics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#e8f5e8', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
            {questionnaires.filter(q => q.status === 'completed').length}
          </div>
          <div>Questionnaires Terminés</div>
        </div>
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>
            {questionnaires.filter(q => q.status === 'in_progress').length}
          </div>
          <div>Questionnaires En Cours</div>
        </div>
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#e8f4f8', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>
            {questionnaires.length}
          </div>
          <div>Total</div>
        </div>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <table className="table" style={{ margin: 0 }}>
          <thead style={{ backgroundColor: '#3498db', color: 'white' }}>
            <tr>
              <th>👤 Nom</th>
              <th>📧 Email</th>
              <th>📝 Questionnaire</th>
              <th>📊 Progression</th>
              <th>🕒 Dernière Mise à Jour</th>
              <th>⚙️ Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuestionnaires.map((q, i) => (
              <tr key={q.partitionKey + q.rowKey || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ fontWeight: 'bold' }}>
                  {q.userName || 'Utilisateur'}
                </td>
                <td style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {q.userId}
                </td>
                <td>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    backgroundColor: q.questionnaireId?.includes('court') ? '#e8f5e8' : '#e8f4f8',
                    color: q.questionnaireId?.includes('court') ? '#27ae60' : '#3498db'
                  }}>
                    {q.questionnaireId?.includes('court') ? '⚡ Court' : '🔍 Complet'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '60px',
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${getProgressPercentage(q)}%`,
                        height: '100%',
                        backgroundColor: getStatusColor(q.status),
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <span style={{
                      fontSize: '0.8rem',
                      color: getStatusColor(q.status),
                      fontWeight: 'bold'
                    }}>
                      {getProgressPercentage(q)}%
                    </span>
                    <span style={{
                      padding: '0.125rem 0.375rem',
                      borderRadius: '8px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      backgroundColor: getStatusColor(q.status),
                      color: 'white'
                    }}>
                      {q.status === 'completed' ? 'Terminé' : 'En cours'}
                    </span>
                  </div>
                </td>
                <td style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                  {q.lastUpdated ? new Date(q.lastUpdated).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '-'}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link
                      to={`/admin/report/${q.userId}/${q.questionnaireId}`}
                      state={{ questionnaireData: q }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.8rem',
                        backgroundColor: '#3498db',
                        border: 'none',
                        textDecoration: 'none',
                        display: 'inline-block',
                        color: 'white'
                      }}
                    >
                      👁️ View
                    </Link>
                    <button
                      className="btn btn-secondary"
                      onClick={() => downloadJSON(q)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.8rem',
                        backgroundColor: '#3498db',
                        border: 'none'
                      }}
                    >
                      📄 JSON
                    </button>

                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredQuestionnaires.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#7f8c8d',
          fontSize: '1.1rem'
        }}>
          {questionnaires.length === 0 ? 
            '📝 Aucun questionnaire trouvé.' : 
            '🔍 Aucun questionnaire ne correspond aux critères de recherche.'
          }
        </div>
      )}
    </div>
  );
};

export default AdminPage;
